import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLang } from '../context/LanguageContext';
import PageHero from '../components/PageHero';
import CTASection from '../components/CTASection';
import FormShell, { Field, inputCls } from '../components/FormShell';
import { submitToSheet, generateSubmissionId, getVisitorId, sanitizePhone } from '../lib/api';
import { Mail, MapPin, Clock } from 'lucide-react';

const Contact = () => {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const initial = {
    full_name: '',
    email: '',
    phone: '',
    country: '',
    company_name: '',
    enquiry_type: '',
    subject: '',
    message: '',
    preferred_contact_method: '',
    consent: false
  };
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [locked, setLocked] = useState(false);
  const [success, setSuccess] = useState(false);
  const [refNo, setRefNo] = useState('');
  const submitLock = useRef(false);

  const set = (k, v) => setForm({ ...form, [k]: v });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.consent || submitLock.current) return;
    submitLock.current = true;
    setSubmitting(true);
    setLocked(true);
    const submission_id = generateSubmissionId('SIB-CONTACT');
    const payload = {
      form_type: 'contact_enquiry',
      submission_id,
      visitor_id: getVisitorId(),
      full_name: form.full_name,
      email: form.email,
      phone: form.phone,
      country: form.country,
      company_name: form.company_name,
      enquiry_type: form.enquiry_type,
      subject: form.subject,
      message: form.message,
      preferred_contact_method: form.preferred_contact_method,
      language: lang,
      page_source: 'contact',
      status: 'submitted'
    };
    await submitToSheet(payload);
    setRefNo(submission_id);
    setSubmitting(false);
    setSuccess(true);
    setTimeout(() => { submitLock.current = false; setLocked(false); }, 4000);
  };

  const handleReset = () => { setSuccess(false); setRefNo(''); setForm(initial); submitLock.current = false; setLocked(false); };

  return (
    <div>
      <PageHero title={t.contact.title} desc={t.contact.desc} />
      <section className="bg-white py-20">
        <div className="sib-container grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-1">
            <h3 className="text-2xl font-bold text-[#0B5D3B] mb-6">{t.contact.infoTitle}</h3>
            <div className="space-y-5">
              {[
                { Icon: MapPin, title: t.contact.hqTitle, value: t.contact.hqValue },
                { Icon: Mail, title: t.contact.emailTitle, value: t.footer.email, ltr: true },
                { Icon: Clock, title: t.contact.hoursTitle, value: t.contact.hoursValue }
              ].map((c, i) => (
                <div key={i} className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-md bg-[#0B5D3B]/10 flex items-center justify-center flex-shrink-0"><c.Icon className="w-5 h-5 text-[#0B5D3B]" /></div>
                  <div>
                    <div className="font-semibold text-[#0B5D3B] text-sm">{c.title}</div>
                    <div className="text-[14px] text-[#33473d]/85 mt-0.5" dir={c.ltr ? 'ltr' : undefined}>{c.value}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-2">
            <FormShell
              title={t.forms.contactTitle}
              desc={t.forms.contactDesc}
              onSubmit={handleSubmit}
              submitting={submitting}
              locked={locked}
              success={success}
              onReset={handleReset}
              refNo={refNo}
              successTitle={t.forms.contactSuccessTitle}
              successBody={t.forms.contactSuccessBody}
              successCta={() => { handleReset(); navigate('/'); }}
              successCtaLabel={t.forms.returnHome}
            >
              <div className="grid md:grid-cols-2 gap-4">
                <Field label={t.forms.fullName} required>
                  <input required className={inputCls} value={form.full_name} onChange={e => set('full_name', e.target.value)} />
                </Field>
                <Field label={t.forms.email} required>
                  <input required type="email" className={inputCls} dir="ltr" value={form.email} onChange={e => set('email', e.target.value)} />
                </Field>
                <Field label={t.forms.phone}>
                  <input
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    pattern="[0-9+\s\-()]{0,20}"
                    title={t.forms.phoneHelp}
                    className={inputCls}
                    dir="ltr"
                    value={form.phone}
                    onChange={e => set('phone', sanitizePhone(e.target.value))}
                    onKeyDown={(e) => { const allowed = /[0-9+\s\-()]/; if (e.key.length === 1 && !allowed.test(e.key) && !e.ctrlKey && !e.metaKey) e.preventDefault(); }}
                  />
                </Field>
                <Field label={t.forms.country} required>
                  <select required className={inputCls} value={form.country} onChange={e => set('country', e.target.value)}>
                    <option value="">{t.forms.selectCountry}</option>
                    {t.forms.countries.map(c => <option key={c}>{c}</option>)}
                  </select>
                </Field>
              </div>
              <Field label={t.forms.companyOrg}>
                <input className={inputCls} value={form.company_name} onChange={e => set('company_name', e.target.value)} />
              </Field>
              <Field label={t.forms.enquiryType} required>
                <select required className={inputCls} value={form.enquiry_type} onChange={e => set('enquiry_type', e.target.value)}>
                  <option value="">{t.forms.selectCountry}</option>
                  {t.forms.enquiryTypes.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </Field>
              <Field label={t.forms.subject} required>
                <input required className={inputCls} value={form.subject} onChange={e => set('subject', e.target.value)} />
              </Field>
              <Field label={t.forms.messageHelp} required>
                <textarea required className={inputCls} rows={4} value={form.message} onChange={e => set('message', e.target.value)} />
              </Field>
              <Field label={t.forms.preferredContact}>
                <select className={inputCls} value={form.preferred_contact_method} onChange={e => set('preferred_contact_method', e.target.value)}>
                  <option value="">{t.forms.selectCountry}</option>
                  <option value="WhatsApp">WhatsApp</option>
                  <option value="Phone Call">{t.forms.phoneCall}</option>
                  <option value="Email">{t.forms.contactMethodEmail}</option>
                </select>
              </Field>
              <label className="flex items-start gap-2 text-sm text-[#33473d]">
                <input type="checkbox" required checked={form.consent} onChange={e => set('consent', e.target.checked)} className="mt-1" />
                <span>{t.forms.consent}</span>
              </label>
            </FormShell>
          </div>
        </div>
      </section>
      <CTASection />
    </div>
  );
};

export default Contact;
